import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { userApi } from "../../../libs/landvoice-api";
import { NS_LOOKUP_QUOTA_CACHE_KEY } from "../constants/cacheKeys";
import { formatNumber } from "../../../utils/numbers";

type CreditsBalance = {
  balance: number | null;
  formattedBalance: string;
};

/**
 * React Query hook for fetching the current user's available credits balance
 * for neighborhood search operations.
 * 
 * @returns UseQueryResult containing both the raw balance and formatted balance
 */
export const useFetchCreditsBalance = (): UseQueryResult<CreditsBalance> => {
  return useQuery({
    queryKey: [NS_LOOKUP_QUOTA_CACHE_KEY],
    queryFn: async () => {
      const response = await userApi.getLookupQuota();
      const quotaApps = response.data;
      
      // Find the neighborhood search or default application quota
      const nsQuota = quotaApps.find(app => 
        app.application === 'neighborhood_search' || 
        app.application === 'geosearch'
      );
      
      // If no specific NS quota found, use the first available quota
      const selectedQuota = nsQuota || quotaApps[0];
      const monthlyQuota = selectedQuota?.quota?.monthly;
      
      if (!monthlyQuota || !monthlyQuota.max) {
        return {
          balance: null,
          formattedBalance: '--'
        };
      }
      
      const balance = monthlyQuota.max - monthlyQuota.used;
      return {
        balance,
        formattedBalance: formatNumber(balance, '--')
      };
    },
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache the data (garbage collect immediately)
    retry: 3,
  });
};